import { useEffect, useState } from "react";
import { X, Loader2, Users } from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";

/**
 * NewChatModal — Wave 19 #85.
 *
 * Manual ad-hoc chat creator. Pick people, optionally link to a customer
 * + entity (JC / ticket / task). Auto-thread generation for entity-bound
 * chats happens server-side on entity creation, so here we focus on
 * "I want to start a fresh group conversation with these 3 staff."
 */
export default function NewChatModal({ onClose, onCreated }) {
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [title, setTitle] = useState("");
  const [memberIds, setMemberIds] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [u, c] = await Promise.all([
          api.get("/users"),
          api.get("/customers").catch(() => ({ data: [] })),
        ]);
        const staff = (u.data || []).filter((x) => x.role !== "client");
        setUsers(staff);
        setCustomers(c.data || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load users.");
      }
    })();
  }, []);

  const filtered = users.filter((u) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  });

  const toggle = (id) => {
    setMemberIds((rows) => rows.includes(id) ? rows.filter((x) => x !== id) : [...rows, id]);
  };

  const submit = async () => {
    if (memberIds.length === 0) {
      toast.error("Pick at least one other member.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/chats", {
        kind: "manual",
        title: title.trim() || undefined,
        member_ids: memberIds,
        customer_id: customerId || undefined,
      });
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create chat.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="new-chat-modal"
    >
      <div className="hv-card p-5 w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Users className="w-5 h-5 text-rose-300" /> New chat
          </h2>
          <button onClick={onClose} className="hv-btn hv-btn-ghost text-xs" data-testid="new-chat-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3 flex-1 overflow-y-auto pr-1">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1 block">Chat name (optional)</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Site A — Pump install team"
              className="hv-input w-full text-sm"
              data-testid="new-chat-title"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1 block">Link to customer (optional)</label>
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className="hv-input w-full text-sm"
              data-testid="new-chat-customer"
            >
              <option value="">— none —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1 block">
              Members ({memberIds.length} selected)
            </label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search staff…"
              className="hv-input w-full text-sm mb-2"
              data-testid="new-chat-search"
            />
            <div className="border border-white/10 rounded-md max-h-64 overflow-y-auto divide-y divide-white/5">
              {filtered.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => toggle(u.id)}
                  className={`w-full text-left p-2.5 hover:bg-white/[0.03] flex items-center justify-between ${
                    memberIds.includes(u.id) ? "bg-rose-500/[0.08]" : ""
                  }`}
                  data-testid={`new-chat-user-${u.id}`}
                >
                  <span>
                    <span className="text-sm font-medium text-white">{u.name}</span>
                    <span className="text-[10px] text-[#647087] ml-2">{u.role}</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={memberIds.includes(u.id)}
                    readOnly
                    className="accent-rose-500"
                  />
                </button>
              ))}
              {filtered.length === 0 && <div className="p-3 text-xs text-[#647087]">No users found.</div>}
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="hv-btn hv-btn-ghost text-xs" data-testid="new-chat-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || memberIds.length === 0}
            className="hv-btn hv-btn-primary text-xs disabled:opacity-40"
            data-testid="new-chat-submit"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            Create chat
          </button>
        </div>
      </div>
    </div>
  );
}
